import { useState, useCallback, useEffect } from "react";
import { SSTFApiClient, TestcaseMappings } from "../services/api";

interface UseTestcaseMappingsProps {
  apiClient?: SSTFApiClient | null;
  workspaceId?: string;
  projectId?: string;
  onAuthError?: () => void;
}

export const useTestcaseMappings = (props?: UseTestcaseMappingsProps) => {
  const [testcaseMappings, setTestcaseMappings] = useState<TestcaseMappings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTestcaseMappings = useCallback(async () => {
    if (!props?.apiClient || !props?.workspaceId || !props?.projectId) {
      setError("API設定が不完全です。設定を確認してください。");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.log("useTestcaseMappings - fetchTestcaseMappings called");

    try {
      console.log("useTestcaseMappings - getTestcaseMappings を呼び出します:", {
        workspaceId: props.workspaceId,
        projectId: props.projectId
      });

      const mappings = await props.apiClient.getTestcaseMappings(
        props.workspaceId,
        props.projectId
      );

      console.log("useTestcaseMappings - fetched mappings:", {
        testTargetCount: mappings.testTargetToTestcases.size,
        usecaseCount: mappings.usecaseToTestcases.size,
        policyCount: mappings.policyToTestcases.size,
      });

      setTestcaseMappings(mappings);
    } catch (err) {
      console.error("useTestcaseMappings - error:", err);

      // 認証エラーの検出
      if (
        err instanceof Error &&
        (err.message.includes("jwt expired") ||
          err.message.includes("トークンの検証に失敗") ||
          err.message.includes("401") ||
          err.message.includes("canActivate"))
      ) {
        console.log("認証エラーを検出しました。APIクライアントをリセットします。");
        if (props?.onAuthError) {
          props.onAuthError();
        }
        setError("認証が無効です。設定画面でトークンを再設定してください。");
      } else {
        setError(
          err instanceof Error ? err.message : "不明なエラーが発生しました"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [props?.apiClient, props?.workspaceId, props?.projectId]);

  // API設定が完了したら自動的にデータを取得
  useEffect(() => {
    if (props?.apiClient && props?.workspaceId && props?.projectId) {
      console.log("API設定が完了したため、自動的にテストケースマッピングを取得します");
      fetchTestcaseMappings();
    } else {
      setLoading(false);
      if (props?.apiClient || props?.workspaceId || props?.projectId) {
        setError("API設定が不完全です。設定を確認してください。");
      } else {
        setError(null);
      }
    }
  }, [props?.apiClient, props?.workspaceId, props?.projectId, fetchTestcaseMappings]);

  return {
    testcaseMappings,
    loading,
    error,
    fetchTestcaseMappings,
  };
};
